








































































import Vue, { VueConstructor } from "vue";
import { mapState, mapActions } from 'vuex'
import * as Tone from 'tone'
import copy from 'copy-to-clipboard'
import { ChordProgression, SetChordParameters, ChordProgressionChord, ChordProgressionChordOrUndefined } from '@/application/models/chordProgression';
import { Inversion, VariantType } from '@/application/models/chords'
import { KeyScaleChord } from '@/application/models/ScaleInKey'
import PlaybackControls from '@/components/PlaybackControls.vue'
import ChordProgressionDisplay from '@/components/ChordProgressionDisplay.vue'
import { queueProgression } from '@/application/TonePlayer'
import { writeMidi } from '@/application/MidiExporter'
import { generateChords, parsimonifyChords } from '@/application/ChordGenerator'
import { setChord, setChords, insertChord, deleteChord, clear, saveEdit } from '@/application/ChordProgressionManager'
import hasBreakpoints, { HasBreakpointsMixinComputed } from '@/mixins/hasBreakpoints'
import hasChordProgression, { HasChordProgressionMixinData } from '@/mixins/hasChordProgression'
import hasScaleInKey, { HasScaleInKeyMixinData } from '@/mixins/hasScaleInKey'
import hasPlaybackSettings, { HasPlaybackSettingsMixin } from '@/mixins/hasPlaybackSettings'
import hasEnharmonics, { HasEnharmonicsMixinData } from '@/mixins/hasEnharmonics'
import IconTrash from '@/assets/icons/icon-trash.svg'
import IconWand from '@/assets/icons/icon-wand.svg'
import IconTrendingUp from '@/assets/icons/icon-trending-up.svg'
import IconEdit from '@/assets/icons/icon-edit.svg'

// TODO do I need this?
interface DropResult<T> {
  removedIndex: number | null
  addedIndex: number
  payload: T
}

type Mixins = HasChordProgressionMixinData & HasBreakpointsMixinComputed & HasScaleInKeyMixinData & HasPlaybackSettingsMixin & HasEnharmonicsMixinData

export default (Vue as VueConstructor<Mixins>).extend({
  name: 'ChordProgressionBuilder',
  mixins: [hasBreakpoints, hasChordProgression, hasScaleInKey, hasPlaybackSettings, hasEnharmonics],
  data() {
    return {
      playing: false,
      numberToGenerate: 4,
      showNameModal: false,
      canShare: !!navigator.share
    }
  },
  components: {
    PlaybackControls,
    ChordProgressionDisplay,
    IconTrash,
    IconWand,
    IconTrendingUp,
    IconEdit
  },
  computed: {
    ...mapState('chordProgression', [
      'progressionName'
    ]),
    ...mapState('sampler', [
      'samplerLoaded',
      'sampler'
    ]),
    disablePlayback(): boolean {
      return !this.samplerLoaded || this.chordProgression.isEmpty
    },
    chords(): (ChordProgressionChordOrUndefined)[] {
      return this.chordProgression.chords.length ? this.chordProgression.chords : [undefined]
    },
    chordDegrees(): number[] {
      return this.chordProgression.chords.filter(c => !!c).map(c => (c as ChordProgressionChord).degree)
    }
  },
  beforeMount() {
    Tone.Transport.stop()
    Tone.Transport.on('start', () => {this.playing = true})
    Tone.Transport.on('stop', () => {this.playing = false})
    Tone.Transport.on('pause', () => {this.playing = false})
  },
  watch: {
    chordProgression: {
      handler(progression: ChordProgression) {
        queueProgression(this.sampler, progression, this.scaleInKey, this.arpeggio, () => {
          if(!this.loop) {
            Tone.Transport.stop()
          }
        })
      },
      immediate: true
    },
    arpeggio() {
      queueProgression(this.sampler, this.chordProgression, this.scaleInKey, this.arpeggio)
    },
    loop: {
      handler(loop: boolean) {
        Tone.Transport.loop = loop
      },
      immediate: true
    },
    bpm: {
      handler(bpm: number) {
        Tone.Transport.bpm.value = bpm
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('chordProgression', [
      'selectIndex',
      'deselectIndex',
    ]),
    async toggle() {
      await Tone.start()
      if(this.chordProgression.isEmpty) return
      
      const state = Tone.Transport.state
      if(state === 'started') {
        Tone.Transport.stop()
        this.$gtag.event('create: stop playback')
      } else {
        Tone.Transport.start('+0.1')
        this.$gtag.event('create: start playback')
      }
    },
    updateVariant(variantType: VariantType<KeyScaleChord>, index: number) {
      const chord = (this.chordProgression as ChordProgression).chords[index]
      if (!chord) return
      setChord(this.$route, this.$router, {
        degree: chord.degree,
        variantType,
        inversion: chord.inversion,
        octave: chord.octave,
        index
      } as SetChordParameters)
    },
    updateInversion(inversion: Inversion, index: number) {
      const chord = (this.chordProgression as ChordProgression).chords[index]
      if (!chord) return
      setChord(this.$route, this.$router, {
        degree: chord.degree,
        variantType: chord.variantType,
        inversion: inversion.number,
        octave: chord.octave,
        index
      } as SetChordParameters)
    },
    updateOctave(octave: number, index: number) {
      const chord = (this.chordProgression as ChordProgression).chords[index]
      if (!chord) return
      setChord(this.$route, this.$router, {
        degree: chord.degree,
        variantType: chord.variantType,
        inversion: chord.inversion,
        octave: octave,
        index
      } as SetChordParameters)
    },
    onDelete(index: number) {
      deleteChord(this.$route, this.$router, index)
    },
    onClear() {
      this.$gtag.event('create: clear progression')
      Tone.Transport.stop()
      clear(this.$route, this.$router)
    },
    focusSlot(index: number) {
      this.selectIndex(index)
    },
    blurSlot(index: number) {
      this.deselectIndex(index)
    },
    onEdit() {
      saveEdit(this.$route, this.$router, true)
    },
    share() {
      if (navigator?.share !== undefined) {
        navigator.share({
          title: 'Chordfish',
          url: this.makeNonEditLink()
        }).catch(err => {
          console.error(err) // not sure there's much I can do with this
        })
        this.$gtag.event('create: share')
      }
    },
    copyUrl() {
      copy(this.makeNonEditLink(), {
        format: 'text/plain',
        onCopy: () => {
          this.$buefy.toast.open('Link copied!')
        }
      })
      
      this.$gtag.event('create: copy url')
    },
    makeNonEditLink() {
      const url = new URL(document.URL)
      url.searchParams.set('edit', 'false')
      return url.toString()
    },
    download() {
      const file = writeMidi(this.chordProgression, this.scaleInKey, this.arpeggio)
      const a = document.createElement('a')
      a.href = file;
      a.setAttribute("download", `${this.progressionName || 'My chord progression'}.mid`);
      a.click();
      this.$gtag.event('create: export midi')
    },
    print() {
      window.print()
      this.$gtag.event('create: print')
    },
    generate() {
      const chords = parsimonifyChords(generateChords(this.chordProgression.chords, this.numberToGenerate), this.scaleInKey)
      setChords(this.$route, this.$router, chords)
      this.$gtag.event('create: generate chords')
    },
    parsimonify () {
      const chords = parsimonifyChords(this.chordProgression.chords, this.scaleInKey)
      setChords(this.$route, this.$router, chords)
      this.$gtag.event('create: parsimonify chords')
    },  
    onDrop (dropResult: DropResult<SetChordParameters>) { // TODO may not need this. I can't remember which dnd library required it
      insertChord(this.$route, this.$router, Object.assign({}, dropResult.payload, { index: dropResult.addedIndex }))
    }
  }
})
